import React, { useState, useMemo } from 'react';
import {
  Table,
  Group,
  Text,
  Badge,
  ActionIcon,
  ScrollArea,
  Checkbox,
  Modal,
  TextInput,
  Stack,
  Select,
  Menu,
  Button,
  Tooltip,
} from '@mantine/core';
import {
  IconEdit,
  IconTrash,
  IconCopy,
  IconDots,
} from '@tabler/icons-react';
import { DatabaseItem, Database } from '../../../types/models/Database';

interface ListViewProps {
  database: Database;
  items: DatabaseItem[];
  visibleProperties?: string[];
  onItemSelect?: (item: DatabaseItem) => void;
  onItemCreate?: (item: DatabaseItem) => void;
  onItemUpdate?: (item: DatabaseItem) => void;
  onItemDelete?: (itemId: string) => void;
  onItemDuplicate?: (item: DatabaseItem) => void;
}

export const ListView: React.FC<ListViewProps> = ({
  database,
  items,
  visibleProperties = [],
  onItemSelect,
  onItemUpdate,
  onItemDelete,
  onItemDuplicate,
}) => {
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [editingItem, setEditingItem] = useState<DatabaseItem | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const displayProperties = useMemo(() => {
    const propsToShow = visibleProperties.length > 0
      ? database.schema.properties.filter(p => visibleProperties.includes(p.id))
      : database.schema.properties.slice(0, 4);

    return [
      { id: 'title', name: 'Title', type: 'title' as const },
      ...propsToShow,
    ];
  }, [database.schema.properties, visibleProperties]);

  const getPropertyValue = (item: DatabaseItem, propId: string): any => {
    if (propId === 'title') return item.title;
    const prop = item.properties.find(p => p.propertyId === propId);
    return prop?.value ?? '-';
  };

  const handleDeleteItem = (itemId: string) => {
    if (confirm('Delete this item?')) {
      onItemDelete?.(itemId);
    }
  };

  const handleSelectAll = () => {
    if (selectedItems.length === items.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(items.map(item => item.id));
    }
  };

  const renderCell = (item: DatabaseItem, propId: string) => {
    const value = getPropertyValue(item, propId);
    const propDef = propId !== 'title' ? database.schema.properties.find(p => p.id === propId) : null;

    if (propDef?.type === 'checkbox') {
      return <Badge color={value ? 'green' : 'gray'}>{value ? 'Yes' : 'No'}</Badge>;
    }

    if (propDef?.type === 'select' || propDef?.type === 'status') {
      const option = propDef?.options?.find(o => o.id === value);
      return <Badge size="sm" variant="light">{option?.name || '-'}</Badge>;
    }

    return (
      <Text size="sm" lineClamp={1}>
        {value}
      </Text>
    );
  };

  return (
    <div className="list-view-wrapper" style={{ width: '100%' }}>
      <ScrollArea style={{ height: 'calc(100vh - 200px)' }}>
        <Table striped highlightOnHover stickyHeader>
          <thead>
            <tr>
              <th style={{ width: '50px' }}>
                <Checkbox
                  checked={selectedItems.length === items.length && items.length > 0}
                  onChange={handleSelectAll}
                />
              </th>
              {displayProperties.map(prop => (
                <th key={prop.id} style={{ minWidth: '150px' }}>
                  <Text size="sm" fw={500}>{prop.name}</Text>
                </th>
              ))}
              <th style={{ width: '100px', textAlign: 'center' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr>
                <td colSpan={displayProperties.length + 2} style={{ textAlign: 'center', padding: '40px' }}>
                  <Text c="dimmed">No items in this view</Text>
                </td>
              </tr>
            ) : (
              items.map(item => (
                <tr
                  key={item.id}
                  style={{
                    backgroundColor: selectedItems.includes(item.id) ? '#f0f0f0' : undefined,
                  }}
                >
                  <td>
                    <Checkbox
                      checked={selectedItems.includes(item.id)}
                      onChange={() =>
                        setSelectedItems(prev =>
                          prev.includes(item.id)
                            ? prev.filter(id => id !== item.id)
                            : [...prev, item.id]
                        )
                      }
                    />
                  </td>
                  {displayProperties.map(prop => (
                    <td key={`${item.id}-${prop.id}`}>
                      {renderCell(item, prop.id)}
                    </td>
                  ))}
                  <td style={{ textAlign: 'center' }}>
                    <Menu shadow="md" position="bottom-end">
                      <Menu.Target>
                        <ActionIcon size="sm" variant="subtle">
                          <IconDots size={14} />
                        </ActionIcon>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Item
                          leftSection={<IconEdit size={14} />}
                          onClick={() => {
                            setEditingItem(item);
                            setIsEditModalOpen(true);
                          }}
                        >
                          Edit
                        </Menu.Item>
                        <Menu.Item
                          leftSection={<IconCopy size={14} />}
                          onClick={() => onItemDuplicate?.(item)}
                        >
                          Duplicate
                        </Menu.Item>
                        <Menu.Divider />
                        <Menu.Item
                          leftSection={<IconTrash size={14} />}
                          color="red"
                          onClick={() => handleDeleteItem(item.id)}
                        >
                          Delete
                        </Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </ScrollArea>

      <Modal
        opened={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        title="Edit Item"
        size="md"
      >
        {editingItem && (
          <Stack>
            <TextInput
              label="Title"
              value={editingItem.title}
              onChange={(e) =>
                setEditingItem({ ...editingItem, title: e.currentTarget.value })
              }
            />
            <Group justify="flex-end" gap="sm">
              <Button variant="subtle" onClick={() => setIsEditModalOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={() => {
                  if (editingItem) {
                    onItemUpdate?.(editingItem);
                    setIsEditModalOpen(false);
                  }
                }}
              >
                Save
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </div>
  );
};

export default ListView;
